import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import Collapse from '@material-ui/core/Collapse'
import MenuItem from '@material-ui/core/MenuItem'
import Tab from '@material-ui/core/Tab'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Tabs from '@material-ui/core/Tabs'
import Typography from '@material-ui/core/Typography'
import FilterListIcon from '@material-ui/icons/FilterList'
import { Pagination } from '@material-ui/lab'
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import NoDataImage from '../../assets/images/No-Data.png'
import PageHeader from '../../components/PageHeader'
import { PAYMENT_FOLLOW_UPS_QUERY } from '../../graphql/paymentFollowUps'
import { formatSessionTime } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import {
  PaymentFollowUp,
  PaymentFollowUpPage,
  PaymentFollowUpReason,
  PaymentFollowUpState
} from '../../types/paymentFollowUps'
import EvidenceSummary from './EvidenceSummary'
import Investigations from './Investigations'
import OutstandingActions from './OutstandingActions'
import ResolveDialog from './ResolveDialog'
import FollowUpAmount from './amount'
import {
  followUpFilter,
  parseReservationNumber,
  readFollowUpFilters,
  writeFollowUpFilters
} from './filters'
import useStyles from './index.styles'
import {
  emptyStateText,
  FOLLOW_UP_REASONS,
  FOLLOW_UP_STATES,
  followUpErrorMessage,
  reasonAction,
  reasonLabel,
  stateLabel
} from './labels'
import { projectionPending, scopedSettlement } from './settlement'

export const FOLLOW_UPS_PER_PAGE = 20
export { followUpFilter, parseReservationNumber } from './filters'

/**
 * Payment follow-ups from adotel: money that moved without the booking following it, for
 * operations to settle. Platform admins only (adotel refuses everyone else). Details and
 * resolutions are shown as plain text: they are free text and must never be rendered as markup.
 */
function PaymentCases() {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const applied = useMemo(
    () => readFollowUpFilters(location.search),
    [location.search]
  )
  const { state, reason, reservationNumber } = applied
  const hasUrlFilters =
    state !== 'Open' || reason !== '' || reservationNumber !== null
  const [filterOpen, setFilterOpen] = useState(hasUrlFilters)
  const [draftState, setDraftState] = useState<PaymentFollowUpState>(state)
  const [draftReason, setDraftReason] = useState<PaymentFollowUpReason | ''>(
    reason
  )
  const [reservationInput, setReservationInput] = useState(
    reservationNumber?.toString() ?? ''
  )
  const [reservationError, setReservationError] = useState(false)
  const [page, setPage] = useState(1)
  const [resolving, setResolving] = useState<PaymentFollowUp | null>(null)
  // The row index whose Resolve button should take focus once the list has reloaded.
  const [focusAfterResolve, setFocusAfterResolve] = useState<number | null>(
    null
  )
  const headingRef = useRef<HTMLHeadingElement>(null)
  const resolveButtons = useRef<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    setFilterOpen(hasUrlFilters)
  }, [location.search, hasUrlFilters])

  useEffect(() => {
    setDraftState(state)
    setDraftReason(reason)
    setReservationInput(reservationNumber?.toString() ?? '')
    setReservationError(false)
    setPage(1)
  }, [state, reason, reservationNumber])

  const { data, loading, error, refetch } = useQuery<{
    paymentFollowUps: PaymentFollowUpPage
  }>(PAYMENT_FOLLOW_UPS_QUERY, {
    variables: {
      filter: followUpFilter(state, reason, reservationNumber),
      page,
      perPage: FOLLOW_UPS_PER_PAGE
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })

  const items = data?.paymentFollowUps.items ?? []
  const total = data?.paymentFollowUps.total ?? 0
  const pages = Math.max(1, Math.ceil(total / FOLLOW_UPS_PER_PAGE))

  useEffect(() => {
    if (focusAfterResolve === null || loading) {
      return
    }
    if (items.length === 0 && page > 1) {
      // The last row of this page was resolved: show the page before it.
      setPage(page - 1)
      return
    }
    const target = Math.min(focusAfterResolve, items.length - 1)
    const button = target >= 0 ? resolveButtons.current[target] : null
    if (button) {
      button.focus()
    } else {
      headingRef.current?.focus()
    }
    setFocusAfterResolve(null)
  }, [focusAfterResolve, loading, items.length, page])

  const applyFilters = (event: FormEvent) => {
    event.preventDefault()
    const parsed =
      reservationInput.trim() === ''
        ? null
        : parseReservationNumber(reservationInput)
    const invalid = reservationInput.trim() !== '' && parsed === null
    setReservationError(invalid)
    if (invalid) return
    history.replace({
      ...location,
      search: writeFollowUpFilters(location.search, {
        state: draftState,
        reason: draftReason,
        reservationNumber: parsed
      })
    })
    setPage(1)
  }

  const clearReservation = () => {
    setReservationInput('')
    setReservationError(false)
    history.replace({
      ...location,
      search: writeFollowUpFilters(location.search, {
        ...applied,
        reservationNumber: null
      })
    })
    setPage(1)
  }

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Payment Follow-ups')} />
      <div className={classes.header}>
        <div>
          <Typography
            variant="h3"
            component="h1"
            ref={headingRef}
            tabIndex={-1}>
            {getTranslate('Payment Follow-ups')}
          </Typography>
          <Typography
            component="p"
            variant="subtitle1"
            className={classes.subTitle}>
            {getTranslate('{count} follow-ups found', { count: total })}
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FilterListIcon />}
          aria-expanded={filterOpen}
          aria-controls="payment-follow-up-filters"
          onClick={() => setFilterOpen(!filterOpen)}>
          {getTranslate('Filter')}
        </Button>
      </div>

      <Collapse in={filterOpen}>
        <form
          id="payment-follow-up-filters"
          className={classes.filters}
          onSubmit={applyFilters}
          noValidate>
          <div className={classes.filterFields}>
            <SelectWithLabel
              id="payment-follow-up-state"
              fullWidth
              size="small"
              noHelperText
              label={getTranslate('State')}
              value={draftState}
              onChange={(event) => {
                setDraftState(event.target.value as PaymentFollowUpState)
              }}
              inputProps={{ 'data-testid': 'follow-up-state' }}>
              {FOLLOW_UP_STATES.map((value) => (
                <MenuItem key={value} value={value}>
                  {stateLabel(value)}
                </MenuItem>
              ))}
            </SelectWithLabel>
            <SelectWithLabel
              id="payment-follow-up-reason"
              fullWidth
              size="small"
              noHelperText
              label={getTranslate('Reason')}
              value={draftReason}
              onChange={(event) => {
                setDraftReason(event.target.value as PaymentFollowUpReason | '')
              }}
              SelectProps={{ displayEmpty: true }}
              inputProps={{ 'data-testid': 'follow-up-reason' }}>
              <MenuItem value="">{getTranslate('All reasons')}</MenuItem>
              {FOLLOW_UP_REASONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {reasonLabel(value)}
                </MenuItem>
              ))}
            </SelectWithLabel>
            <div>
              <InputWithLabel
                id="payment-follow-up-reservation"
                fullWidth
                inputSize="small"
                label={getTranslate('Reservation Number')}
                value={reservationInput}
                onChange={(event) =>
                  setReservationInput(event.currentTarget.value)
                }
                error={reservationError}
                noHelperText
                inputMode="numeric"
                dir="ltr"
                aria-invalid={reservationError}
                aria-describedby={
                  reservationError
                    ? 'payment-follow-up-reservation-error'
                    : undefined
                }
              />
              {reservationError && (
                <Typography
                  id="payment-follow-up-reservation-error"
                  variant="caption"
                  color="error"
                  role="alert">
                  {getTranslate('Enter a valid reservation number.')}
                </Typography>
              )}
            </div>
          </div>
          <div className={classes.filterActions}>
            <Button
              type="button"
              color="secondary"
              onClick={() => {
                setDraftState('Open')
                setDraftReason('')
                setReservationInput('')
                setReservationError(false)
              }}>
              {getTranslate('Reset')}
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {getTranslate('Apply')}
            </Button>
          </div>
        </form>
      </Collapse>

      {reservationNumber !== null && (
        <div className={classes.appliedFilters}>
          <Chip
            label={
              <span>
                {getTranslate('Reservation Number')}:{' '}
                <bdi>{reservationNumber}</bdi>
              </span>
            }
            onDelete={clearReservation}
            aria-label={`${getTranslate('Clear')} ${getTranslate(
              'Reservation Number'
            )}`}
          />
        </div>
      )}

      <Typography component="p" color="textSecondary">
        {getTranslate(
          'These are settlement reviews. The payment amount is not proof of the amount still owed. Verify previous refunds, provider reversals and any accepted wallet settlement before repaying.'
        )}
      </Typography>

      {error && (
        <Typography color="error" role="alert">
          {followUpErrorMessage(error)}
        </Typography>
      )}

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress
            size={36}
            role="status"
            aria-label={getTranslate('Loading')}
          />
        </div>
      ) : !error && items.length === 0 ? (
        <div className={classes.emptyState}>
          <img src={NoDataImage} alt="" className={classes.emptyImage} />
          <div className={classes.emptyText}>{emptyStateText(state)}</div>
          <div className={classes.emptyText}>
            {getTranslate('Try adjusting your filters')}
          </div>
        </div>
      ) : (
        items.length > 0 && (
          <div className={classes.tableWrapper}>
            <Table size="small" aria-label={getTranslate('Payment Follow-ups')}>
              <TableHead>
                <TableRow>
                  <TableCell>{getTranslate('Reason')}</TableCell>
                  <TableCell>{getTranslate('Reservation Number')}</TableCell>
                  <TableCell>{getTranslate('Payment number')}</TableCell>
                  <TableCell>{getTranslate('Rooms')}</TableCell>
                  <TableCell>{getTranslate('Payment amount')}</TableCell>
                  <TableCell>{getTranslate('Amount under review')}</TableCell>
                  <TableCell>{getTranslate('Gateway')}</TableCell>
                  <TableCell>{getTranslate('Opened at')}</TableCell>
                  <TableCell>{getTranslate('Details')}</TableCell>
                  <TableCell>{getTranslate('Resolution')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((followUp, index) => (
                  <TableRow key={followUp.id}>
                    <TableCell>
                      {reasonLabel(followUp.reason)}
                      <Typography
                        variant="caption"
                        className={classes.reasonAction}>
                        {reasonAction(
                          followUp.reason,
                          followUp.gateway,
                          followUp.requiredAction
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {followUp.displayReservationNumber &&
                      followUp.bookingReference ? (
                        <LocalizedLink
                          to={`/booking/list/${followUp.bookingReference}/view`}>
                          <bdi>{followUp.displayReservationNumber}</bdi>
                        </LocalizedLink>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <bdi>{followUp.paymentNumber}</bdi>
                    </TableCell>
                    <TableCell>
                      <bdi>{followUp.crsNumbers.join(', ') || '-'}</bdi>
                    </TableCell>
                    <TableCell>
                      <FollowUpAmount
                        amount={followUp.amount}
                        currency={followUp.currency}
                      />
                    </TableCell>
                    <TableCell>
                      {scopedSettlement(followUp) ||
                      projectionPending(followUp) ? (
                        <OutstandingActions followUp={followUp} />
                      ) : followUp.amountUnderReview == null ? (
                        getTranslate('Needs reconciliation')
                      ) : (
                        <FollowUpAmount
                          amount={followUp.amountUnderReview}
                          currency={followUp.currency}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <bdi>
                        {followUp.gateway ?? '-'}
                        {followUp.gatewayPaymentType
                          ? ` (${followUp.gatewayPaymentType})`
                          : ''}
                      </bdi>
                    </TableCell>
                    <TableCell>
                      {formatSessionTime(followUp.openedAt)}
                    </TableCell>
                    <TableCell>
                      <div dir="auto" className={classes.freeText}>
                        {followUp.details}
                      </div>
                      <EvidenceSummary evidence={followUp.evidence} />
                    </TableCell>
                    <TableCell>
                      {followUp.state === 'Open' ? (
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          disabled={projectionPending(followUp)}
                          ref={(element: HTMLButtonElement | null) => {
                            resolveButtons.current[index] = element
                          }}
                          aria-label={
                            (followUp.caseSchemaVersion ?? 1) >= 2
                              ? getTranslate(
                                  'Record settlement for payment {number}',
                                  { number: followUp.paymentNumber }
                                )
                              : getTranslate('Resolve payment {number}', {
                                  number: followUp.paymentNumber
                                })
                          }
                          onClick={() => setResolving(followUp)}>
                          {(followUp.caseSchemaVersion ?? 1) >= 2
                            ? getTranslate('Record settlement')
                            : getTranslate('Resolve')}
                        </Button>
                      ) : (
                        <div>
                          <div dir="auto" className={classes.freeText}>
                            {followUp.resolution}
                          </div>
                          <Typography variant="caption">
                            {getTranslate('Resolved by')}:{' '}
                            <bdi>{followUp.resolvedBy ?? '-'}</bdi>
                            {' · '}
                            {getTranslate('Resolved at')}:{' '}
                            {formatSessionTime(followUp.resolvedAt)}
                          </Typography>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      )}

      {pages > 1 && (
        <div className={classes.paginationContainer}>
          <Pagination
            count={pages}
            page={page}
            color="primary"
            onChange={(_, value) => setPage(value)}
          />
        </div>
      )}

      {resolving && (
        <ResolveDialog
          followUp={resolving}
          onClose={() => setResolving(null)}
          onResolved={async () => {
            const index = items.indexOf(resolving)
            setResolving(null)
            // Only once the list shows the result: the resolved row may have left it.
            try {
              await refetch()
              setFocusAfterResolve(Math.max(index, 0))
            } catch {
              // useQuery renders the refresh error. The resolution already succeeded;
              // do not retry it or leave an unhandled rejection/focus in the closed dialog.
              headingRef.current?.focus()
            }
          }}
        />
      )}
    </div>
  )
}

export default function MonitoringPaymentFollowUpsTab() {
  const history = useHistory()
  const location = useLocation()
  const investigation =
    new URLSearchParams(location.search).get('view') === 'investigations'
  return (
    <>
      <Tabs
        value={investigation ? 'investigations' : 'cases'}
        variant="scrollable"
        scrollButtons="auto"
        aria-label={getTranslate('Payment review views')}
        onChange={(_, value: string) => {
          const params = new URLSearchParams(location.search)
          if (value === 'investigations') params.set('view', value)
          else params.delete('view')
          history.replace({ ...location, search: `?${params.toString()}` })
        }}>
        <Tab
          id="payment-cases-tab"
          aria-controls="payment-cases-panel"
          value="cases"
          label={getTranslate('Settlement cases')}
        />
        <Tab
          id="payment-investigations-tab"
          aria-controls="payment-investigations-panel"
          value="investigations"
          label={getTranslate('Investigations')}
        />
      </Tabs>
      <div
        role="tabpanel"
        id={
          investigation ? 'payment-investigations-panel' : 'payment-cases-panel'
        }
        aria-labelledby={
          investigation ? 'payment-investigations-tab' : 'payment-cases-tab'
        }>
        {investigation ? <Investigations /> : <PaymentCases />}
      </div>
    </>
  )
}
